import { ChevronDown, MessageCircle, Send, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

const IDLE_COLLAPSE_MS = 5000;
const MAX_VISIBLE_MESSAGES = 4;

export interface SolvysChatDrawerMessage {
  id: string;
  agentName: string;
  summary: string;
}

interface SolvysChatDrawerProps {
  messages: SolvysChatDrawerMessage[];
  isOpen: boolean;
  onOpen: () => void;
  onCollapse: () => void;
  onClear: () => void;
  onSendMessage: (message: string) => void;
}

export function SolvysChatDrawer({
  messages,
  isOpen,
  onOpen,
  onCollapse,
  onClear,
  onSendMessage,
}: SolvysChatDrawerProps) {
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestMessage = messages[messages.length - 1];
  const visibleMessages = useMemo(
    () => messages.slice(-MAX_VISIBLE_MESSAGES),
    [messages],
  );

  const clearCollapseTimer = useCallback(() => {
    if (!collapseTimerRef.current) return;
    clearTimeout(collapseTimerRef.current);
    collapseTimerRef.current = null;
  }, []);

  const restartCollapseTimer = useCallback(() => {
    clearCollapseTimer();
    if (messages.length === 0) return;
    collapseTimerRef.current = setTimeout(() => {
      onCollapse();
      collapseTimerRef.current = null;
    }, IDLE_COLLAPSE_MS);
  }, [clearCollapseTimer, messages.length, onCollapse]);

  const registerActivity = useCallback(() => {
    if (messages.length === 0) return;
    restartCollapseTimer();
  }, [messages.length, restartCollapseTimer]);

  useEffect(() => {
    if (!isOpen || messages.length === 0) {
      clearCollapseTimer();
      return;
    }
    restartCollapseTimer();
    return clearCollapseTimer;
  }, [clearCollapseTimer, isOpen, messages.length, restartCollapseTimer]);

  useEffect(() => clearCollapseTimer, [clearCollapseTimer]);

  if (!latestMessage) return null;

  if (!isOpen) {
    return (
      <div className="pointer-events-none absolute right-2 top-2 z-50">
        <button
          type="button"
          className="pointer-events-auto flex h-10 w-[188px] max-w-[calc(100vw-1rem)] items-center gap-2 overflow-hidden rounded-md border border-[#c79f4a]/25 bg-[#090704]/95 px-2.5 text-left shadow-xl shadow-black/35 backdrop-blur-xl transition-colors hover:border-[#c79f4a]/45 hover:bg-[#0d0b05]"
          onClick={() => {
            onOpen();
            restartCollapseTimer();
          }}
          aria-label="Open Solvys chat drawer"
          title="Open Solvys chat drawer"
        >
          <MessageCircle size={14} className="shrink-0 text-[#c79f4a]" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[10px] font-semibold uppercase tracking-[0.12em] text-[#c79f4a]">
              Solvys chat
            </span>
            <span className="block truncate text-[10px] text-[#f0ead6]/55">
              {latestMessage.agentName}
            </span>
          </span>
          <span className="grid h-5 min-w-5 place-items-center rounded border border-[#c79f4a]/20 px-1 text-[10px] text-[#f0ead6]/60">
            <FintheonNumericText text={messages.length} kind="date-time" />
          </span>
        </button>
      </div>
    );
  }

  return (
    <div
      className="pointer-events-none absolute right-2 top-2 z-50"
      onPointerMove={registerActivity}
      onFocusCapture={registerActivity}
      onKeyDown={registerActivity}
    >
      <section
        className="pointer-events-auto flex w-[280px] max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-md border border-[#c79f4a]/24 bg-[#090704]/96 shadow-2xl shadow-black/45 backdrop-blur-xl"
        aria-label="Solvys chat drawer"
      >
        <header className="flex h-9 items-center gap-2 border-b border-[#c79f4a]/14 px-2.5">
          <MessageCircle size={14} className="shrink-0 text-[#c79f4a]" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[10px] font-semibold uppercase tracking-[0.12em] text-[#c79f4a]">
              Solvys chat
            </p>
            <p className="truncate text-[10px] text-[#f0ead6]/45">
              <FintheonNumericText text={messages.length} kind="date-time" />{" "}
              relay{messages.length === 1 ? "" : "s"}
            </p>
          </div>
          <button
            type="button"
            className="grid h-6 w-6 shrink-0 place-items-center rounded border border-transparent text-[#f0ead6]/40 transition-colors hover:border-[#c79f4a]/20 hover:text-[#f0ead6]/75"
            onClick={onCollapse}
            aria-label="Collapse Solvys chat drawer"
            title="Collapse"
          >
            <ChevronDown size={13} />
          </button>
          <button
            type="button"
            className="grid h-6 w-6 shrink-0 place-items-center rounded border border-transparent text-[#f0ead6]/35 transition-colors hover:border-[#c79f4a]/20 hover:text-[#f0ead6]/75"
            onClick={() => {
              clearCollapseTimer();
              onClear();
            }}
            aria-label="Clear Solvys chat drawer"
            title="Clear"
          >
            <X size={13} />
          </button>
        </header>

        <div
          className="flex max-h-[224px] flex-col gap-2 overflow-y-auto px-2.5 py-2"
          role="log"
          aria-live="polite"
        >
          {visibleMessages.map((message) => (
            <article
              key={message.id}
              className="rounded-md border border-[#c79f4a]/14 bg-[#0d0b05]/80 px-2 py-1.5"
            >
              <div className="mb-1 flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-[10px] font-semibold text-[#c79f4a]">
                  {message.agentName}
                </span>
                <button
                  type="button"
                  className="grid h-5 w-5 shrink-0 place-items-center rounded border border-[#c79f4a]/18 text-[#f0ead6]/45 transition-colors hover:border-[#c79f4a]/40 hover:text-[#f0ead6]/80"
                  onClick={() => {
                    onSendMessage(
                      `Follow up on ${message.agentName}'s finding:\n\n${message.summary}`,
                    );
                    onCollapse();
                  }}
                  aria-label={`Send ${message.agentName} finding to chat`}
                  title="Send to chat"
                >
                  <Send size={11} />
                </button>
              </div>
              <p className="max-h-[54px] overflow-hidden break-words text-[10px] leading-relaxed text-[#f0ead6]/62">
                {message.summary}
              </p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
